import { FormStepper } from "@common/components/stepper/FormStepper"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { congratsTypes } from "@common/screens/CongratsScreen"
import { theme } from "@common/theme/theme"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { putCongrats } from "@data/redux/actions/navigation"
import { liveService } from "@data/services/liveService"
import { formatOccurrenceDate, whatIsIt } from "@data/utility/Utils"
import { validation } from "@data/utility/validation"
import { useNavigation } from "@react-navigation/native"
import React, { useEffect, useState } from "react"
import { View } from "react-native"
import { ActivityIndicator } from "react-native-paper"
import { connect, useSelector } from "react-redux"
import { findClosestNextOccurrence } from "../account/onboarding/LiveRoomCard"
import RecoverLiveSessionComponent from "./RecoverLiveSessionComponent"

const AbsenceFormComponent = (props) => {
    const {
        liveSessionId,
        liveRoomId,
        withStudent = null,
        occurrenceDate = null,
    } = props.route.params

    const [formValues, setFormValues] = useState(null)
    const navigation = useNavigation()
    const defaultValues = {}

    // If occurrence is pre-selected, skip the occurrence selection step
    const hasPreselectedOccurrence = !!occurrenceDate

    const asParent =
        whatIsIt(withStudent) === "String"
            ? withStudent === "true"
            : withStudent
    const user = !asParent ? props.user : props.formDataUser
    const liveSessions = useSelector((state) => state.data.liveSessions?.list)
    const closestNextLiveSessionOccurrence = findClosestNextOccurrence([
        liveSessions?.find((x) => parseInt(x?.id) === parseInt(liveSessionId)),
    ])

    const getService = !asParent
        ? liveService.getAllMyNextLiveSessions
        : liveService.getAllMyNextLiveSessionsAsParent
    const params = !asParent ? {} : { student_email: user?.email }

    useEffect(() => {
        props.apiRequest(
            getService,
            {},
            {},
            { live_room_id: liveRoomId, ...params }
        )
    }, [])

    const situations = [
        { label: "Je vais être en retard", value: "late" },
        {
            label: "J'ai besoin de décaler la séance",
            value: "absent",
            description: "Cliquer ici en cas d'absence ponctuelle",
        },
    ]

    const lateOptions = [
        { label: "5 min", value: "5" },
        { label: "10 min", value: "10" },
        { label: "15 min", value: "15" },
        { label: "20 min", value: "20" },
        { label: "30 min", value: "30" },
    ]

    const absenceFlagsDates = liveSessions?.flatMap((session) =>
        session?.attendance_flags
            ?.filter(
                (flag) => flag.user_id === user.id && flag.type === "absent"
            )
            ?.map((flag) => flag.date)
    )

    const lateFlagDates = liveSessions?.flatMap((session) =>
        session?.attendance_flags
            ?.filter((flag) => flag.user_id === user.id && flag.type === "late")
            ?.map((flag) => flag.date)
    )

    const absenceOptions = liveSessions
        ?.flatMap((liveSession) => {
            return liveSession?.next_occurrences?.map((occurrence) => {
                const occurrenceDate = formatOccurrenceDate(occurrence?.date)
                return {
                    label: occurrenceDate,
                    value: occurrence.date,
                    liveSessionId: liveSession.id,
                }
            })
        })
        ?.filter(
            (option) =>
                !absenceFlagsDates?.includes(option.value) &&
                (formValues?.type !== "late" ||
                    !lateFlagDates?.includes(option.value))
        )
        ?.sort((a, b) => new Date(a.value) - new Date(b.value))
        ?.map(({ label, value }) => ({ label, value }))

    const selectedLive = liveSessions?.find((liveSession) =>
        liveSession?.next_occurrences?.some(
            (occurrence) => occurrence?.date === occurrenceDate
        )
    )

    const formData = [
        {
            label: "Décris ta situation",
            description: "",
            name: "type",
            type: "select.single",
            options: situations,
            defaultValue: "",
            validationRules: { ...validation.required() },
        },
        {
            is_masked: formValues?.type !== "late",
            label: "Je vais être en retard de",
            description: "",
            name: "late_minutes",
            type: "select.single",
            options: lateOptions,
            defaultValue: null,
            validationRules: { ...validation.required() },
        },
        {
            is_masked: hasPreselectedOccurrence,
            label:
                formValues?.type === "late"
                    ? `Je vais être en retard de ${formValues?.late_minutes} min à la séance du`
                    : "Je vais être absent.e à la séance du",
            description: "",
            name: "occurrence_date",
            type: "select.single",
            options: absenceOptions,
            defaultValue: hasPreselectedOccurrence
                ? occurrenceDate
                : !absenceFlagsDates?.includes(
                        closestNextLiveSessionOccurrence?.date
                    )
                  ? closestNextLiveSessionOccurrence?.date
                  : null,
            validationRules: { ...validation.required() },
        },
        {
            is_masked: formValues?.type === "late",
            label: null,
            description: null,
            type: "custom",
            name: "recovery_occurrence_id",
            component: RecoverLiveSessionComponent,
            componentProps: {
                asParent,
                student: user,
                liveSessionId: selectedLive?.id || liveSessionId,
                occurrenceDate: occurrenceDate,
            },
        },
    ]

    const onSubmit = (data) => {
        if (
            props.reportAbsenceAsStudentRequest ||
            props.reportAbsenceAsParentRequest
        )
            return
        if (!asParent)
            props.apiRequest(
                liveService.reportAbsenceAsStudent,
                { id: selectedLive?.id },
                {
                    ...data,
                    // Ensure occurrence_date is always sent (it may be missing if the field was masked)
                    occurrence_date: data.occurrence_date || occurrenceDate,
                }
            )
        else
            props.apiRequest(
                liveService.reportAbsenceAsParent,
                { id: selectedLive?.id },
                {
                    ...data,
                    // Ensure occurrence_date is always sent (it may be missing if the field was masked)
                    occurrence_date: data.occurrence_date || occurrenceDate,
                    student_email: props.formDataUser?.email,
                }
            )
    }
    useApiStatus(
        !asParent
            ? liveService.reportAbsenceAsStudent
            : liveService.reportAbsenceAsParent, // api service
        null, // success message on toast (string or null)
        true, // error message on toast (true) or keep it in redux state (false)
        () => {
            props.apiRequest(getService, {}, {}, params)

            const congrats = [
                {
                    congratsType: congratsTypes.GENERIC,
                    congratsData: {
                        title: "Effectué",
                        text: `${
                            formValues?.type === "late"
                                ? "Le retard a bien été signalé"
                                : "L'absence a bien été signalée"
                        }`,
                    },
                },
            ]

            props.putCongrats(congrats)
            navigation.replace("congrats")
        },
        () => {}
    )

    return (
        <View
            style={{
                flex: 1,
                alignItems: "center",
            }}
        >
            {props.getAllMyNextLiveSessionsRequest ||
            props.getAllMyNextLiveSessionsAsParentRequest ? (
                <ActivityIndicator
                    animating={true}
                    style={{
                        marginTop: 50,
                    }}
                    size="small"
                    color={theme.colors.primary}
                />
            ) : (
                <FormStepper
                    navigation={navigation}
                    track
                    formData={formData?.filter((x) => !x.is_masked)}
                    champion={null}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    defaultValues={defaultValues}
                    onSubmit={onSubmit}
                    submitted={
                        props.reportAbsenceAsStudentRequest ||
                        props.reportAbsenceAsParentRequest
                    }
                    previousRouteName={"menu"}
                    submittedMessage=""
                    submitButtonText={`Signaler ${
                        formValues?.type === "late" ? "le retard" : "l'absence"
                    }`}
                    serverError={
                        props.reportAbsenceAsStudentError ||
                        props.reportAbsenceAsParentError
                    }
                />
            )}
        </View>
    )
}
const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        formDataUser: state.data.formData?.user,
        getUserRequest: state.api.getUser?.request,
        getAllMyNextLiveSessionsRequest:
            state.api.getAllMyNextLiveSessions?.request,
        getAllMyNextLiveSessionsAsParentRequest:
            state.api.getAllMyNextLiveSessionsAsParent?.request,
        reportAbsenceAsStudentRequest:
            state.api.reportAbsenceAsStudent?.request,
        reportAbsenceAsStudentError: state.api.reportAbsenceAsStudent?.error,
        reportAbsenceAsParentRequest: state.api.reportAbsenceAsParent?.request,
        reportAbsenceAsParentError: state.api.reportAbsenceAsParent?.error,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    putCongrats,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(AbsenceFormComponent)
